import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import {
  CalendarDays,
  ChevronDown,
  LayoutDashboard,
  LogIn,
  LogOut,
  Sparkles,
  UserRound,
  type LucideIcon,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router';

import { Button } from '@/components/ui/button';
import { signOut, useSession } from '@/lib/auth-client';
import { cn } from '@/lib/utils';
import type { Role } from '@shared/schemas/user';

/*
 * The header's account control — *owner feedback, 2026-08-04.*
 *
 * **It used to say "My Schedule" and go to the sign-in page.** That is what the
 * Airo site's header says, so 8.4 ported it, and porting it was the wrong call:
 * §8.5's "port the copy, do not paraphrase" is about the marketing prose — the
 * words that are the business's voice — and this is a navigation affordance.
 * A visitor who has never signed in reads "My Schedule", clicks it expecting a
 * calendar, and lands on a login form. Somebody who *is* signed in clicks it
 * and lands on a dashboard the label never mentioned.
 *
 * So the label now says what the control does, and what it does depends on
 * whether anybody is signed in:
 *
 *   - **Signed out** → "Sign in". One link, no menu.
 *   - **Signed in** → "My account", opening the handful of places that person
 *     actually goes, chosen by role.
 *
 * The footer still says "Client Portal", which is *its* own captured wording and
 * is unambiguous — it names the destination.
 */

interface Shortcut {
  label: string;
  to: string;
  icon: LucideIcon;
}

/**
 * Where each role actually goes.
 *
 * Four at most. A menu that lists every page is a second navigation to
 * maintain and is slower to use than the sidebar the portal already has — the
 * point of this one is the two or three destinations somebody wants *from the
 * public site*, having arrived there rather than at their portal.
 */
const ADMIN_SHORTCUTS: readonly Shortcut[] = [
  { label: 'Dashboard', to: '/admin', icon: LayoutDashboard },
  { label: 'Jobs', to: '/admin/jobs', icon: Sparkles },
  { label: 'Calendar', to: '/admin/calendar', icon: CalendarDays },
  { label: 'Clients', to: '/admin/clients', icon: UserRound },
];

const SHORTCUTS: Record<Role, readonly Shortcut[]> = {
  client: [
    { label: 'Dashboard', to: '/portal', icon: LayoutDashboard },
    { label: 'My cleans', to: '/portal/cleans', icon: CalendarDays },
    { label: 'Request a cleaning', to: '/portal/request', icon: Sparkles },
    { label: 'My profile', to: '/portal/profile', icon: UserRound },
  ],
  employee: [
    { label: 'Today', to: '/employee', icon: LayoutDashboard },
    { label: 'My jobs', to: '/employee/jobs', icon: Sparkles },
    { label: 'My schedule', to: '/employee/schedule', icon: CalendarDays },
    { label: 'My profile', to: '/employee/profile', icon: UserRound },
  ],
  admin: ADMIN_SHORTCUTS,
  // Identical on purpose — the extra permissions are inside the portal, not a
  // different set of places to go.
  super_admin: ADMIN_SHORTCUTS,
};

const ITEM_CLASS = cn(
  'flex min-h-touch cursor-pointer select-none items-center gap-2.5 rounded-md px-3 text-body text-ink-900',
  'outline-none data-[highlighted]:bg-honey-50 data-[highlighted]:text-bark-900',
);

export function AccountMenu({ onNavigate }: { onNavigate?: () => void }) {
  const { data: session, isPending } = useSession();
  const navigate = useNavigate();

  /*
   * **While the session is resolving, show the signed-out control.**
   *
   * The alternative — a spinner or a blank slot — makes the header jump on
   * every page load for the overwhelmingly common case, which is a visitor who
   * is not signed in at all. "Sign in" is also the right thing to show if the
   * request never resolves.
   */
  if (isPending || !session) {
    return (
      <Button asChild variant="ghost">
        <Link to="/login" onClick={onNavigate}>
          <LogIn aria-hidden />
          Sign in
        </Link>
      </Button>
    );
  }

  const role = session.user.role;
  const shortcuts = SHORTCUTS[role];

  async function onSignOut(): Promise<void> {
    await signOut();
    // Back to the public home, not to /login. Somebody signing out *from the
    // marketing site* was reading the marketing site.
    await navigate('/', { replace: true });
  }

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <Button variant="ghost">
          <UserRound aria-hidden />
          My account
          {/* Rotates when open, so the control says whether it is expanded
              rather than only announcing it. */}
          <ChevronDown
            aria-hidden
            className="transition-transform duration-200 data-[state=open]:rotate-180"
          />
        </Button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={8}
          className={cn(
            'z-50 min-w-[15rem] rounded-lg border border-ink-300 bg-white p-1.5 shadow-pop',
            // Radix sets `data-state` and `data-side`; the animation is
            // motion-safe so it simply appears for anybody who asked for that.
            'motion-safe:data-[state=open]:animate-in motion-safe:data-[state=open]:fade-in-0',
            'motion-safe:data-[state=open]:zoom-in-95 motion-safe:data-[state=open]:slide-in-from-top-1',
          )}
        >
          {/* Who is signed in. Without it, an office sharing a machine cannot
              tell whose account they are about to act as. */}
          <div className="px-3 py-2">
            <p className="truncate text-label text-ink-900">{session.user.name}</p>
            <p className="text-meta capitalize text-ink-500">{role}</p>
          </div>

          <DropdownMenu.Separator className="my-1 h-px bg-ink-300" />

          {shortcuts.map((shortcut) => (
            <DropdownMenu.Item key={shortcut.to} asChild className={ITEM_CLASS}>
              <Link to={shortcut.to} onClick={onNavigate}>
                <shortcut.icon aria-hidden className="size-[18px] text-bark-600" />
                {shortcut.label}
              </Link>
            </DropdownMenu.Item>
          ))}

          <DropdownMenu.Separator className="my-1 h-px bg-ink-300" />

          <DropdownMenu.Item
            className={ITEM_CLASS}
            onSelect={() => {
              void onSignOut();
            }}
          >
            <LogOut aria-hidden className="size-[18px] text-bark-600" />
            Sign out
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

/**
 * The same thing for the mobile drawer, flattened.
 *
 * A dropdown inside a slide-over is a menu inside a menu — two escape keys deep
 * and easy to get lost in on a phone. The drawer has room, so the shortcuts are
 * simply listed.
 */
export function AccountLinks({ onNavigate }: { onNavigate?: () => void }) {
  const { data: session, isPending } = useSession();
  const navigate = useNavigate();

  if (isPending || !session) {
    return (
      <Link
        to="/login"
        onClick={onNavigate}
        className="flex min-h-touch items-center gap-2.5 rounded-md px-3 text-label text-bark-700 hover:bg-honey-50"
      >
        <LogIn aria-hidden className="size-[18px]" />
        Sign in
      </Link>
    );
  }

  const role = session.user.role;

  async function onSignOut(): Promise<void> {
    await signOut();
    await navigate('/', { replace: true });
  }

  return (
    <>
      <p className="px-3 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-[0.06em] text-ink-500">
        {session.user.name}
      </p>

      {SHORTCUTS[role].map((shortcut) => (
        <Link
          key={shortcut.to}
          to={shortcut.to}
          onClick={onNavigate}
          className="flex min-h-touch items-center gap-2.5 rounded-md px-3 text-label text-bark-700 hover:bg-honey-50"
        >
          <shortcut.icon aria-hidden className="size-[18px]" />
          {shortcut.label}
        </Link>
      ))}

      <button
        type="button"
        onClick={() => {
          void onSignOut();
        }}
        className="flex min-h-touch w-full items-center gap-2.5 rounded-md px-3 text-left text-label text-bark-700 hover:bg-honey-50"
      >
        <LogOut aria-hidden className="size-[18px]" />
        Sign out
      </button>
    </>
  );
}
